import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Table2, BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { KpiStrip, MetricCard, Panel } from '@/components/erp/primitives'
import { ChartFrame, RankedBars } from '@/shared/ui/charts'
import { Donut, type DonutSlice } from '@/shared/charts/Donut'
import { EmptyState, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import {
  analyticsGeography, analyticsResponseTimes, analyticsSources, analyticsSummary,
  analyticsTags, analyticsTrend, mapMarkers,
  type AnalyticsSummary, type GeographyRow, type MapMarker, type ResponseTimes,
  type SourceRow, type TagRow, type TrendPoint,
} from './api'
import { AskPanel, CouncilBoard } from '@/shared/ui/council'
import { advise, ask, incidentContext } from './analytics/council'
import { IncidentMap } from './analytics/IncidentMap'
import {
  SEVERITY_ORDER, STATUS_FLOW, VERIFICATION_ORDER, inScaleOrder, label,
} from './vocab'

/**
 * Incident analytics.
 *
 * ## The palette decision, made once
 *
 * - **Category, source outlet, tag** are IDENTITY → the categorical `--viz-*` set,
 *   assigned in fixed order and never cycled. Validated: light worst adjacent CVD ΔE 9.1,
 *   dark 8.4. Three light steps sit under 3:1 on the card, which is why every chart here
 *   also carries its values in text and offers a table view — the contrast warning is
 *   discharged by relief, not dismissed.
 * - **Severity and verification** are ORDERED → the `--ordinal-*` ramp, drawn in scale
 *   order and never re-sorted by size. Ordered data on a categorical palette is the most
 *   common chart error there is, and severity is where it does the most damage.
 * - **Status** is a STATE → the funnel's ordinal steps with the stage named beside each
 *   bar. Never colour alone.
 *
 * ## One axis, always
 *
 * The trend shows total and critical on the same count axis, with critical drawn INSIDE
 * the total bar because critical incidents are a subset of them. Stacking the two would
 * add a critical incident to the chart twice.
 *
 * ## from/to are optional on every call
 *
 * A browser cannot know when polling day was. Null means unbounded and the server picks
 * its own window; the range control below narrows it when somebody wants that.
 */

type Tab = 'pressure' | 'geography' | 'patterns' | 'response' | 'council'

const TABS: { id: Tab; label: string; hint: string }[] = [
  { id: 'pressure',  label: 'Pressure',   hint: 'What is on the desk and how it is moving' },
  { id: 'geography', label: 'Geography',  hint: 'Where the reports are coming from' },
  { id: 'patterns',  label: 'Patterns',   hint: 'Category, severity, verification, who told us' },
  { id: 'response',  label: 'Response',   hint: 'How fast the desk actually moves' },
  { id: 'council',   label: 'AI Council', hint: 'The desk read as decisions' },
]

const RANGES = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 0, label: 'All time' },
]

export function AnalyticsPage() {
  const [days, setDays] = useState(90)
  const [tab, setTab] = useState<Tab>('pressure')
  // Severity and category narrow EVERY call, not just the chart in front of you — a board
  // where one panel is filtered and the rest are not is a board that quietly disagrees
  // with itself.
  const [severity, setSeverity] = useState<string | null>(null)
  const [category, setCategory] = useState<string | null>(null)
  const [markers, setMarkers] = useState<MapMarker[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
  const [trend, setTrend] = useState<TrendPoint[]>([])
  const [geography, setGeography] = useState<GeographyRow[]>([])
  const [sources, setSources] = useState<SourceRow[]>([])
  const [tags, setTags] = useState<TagRow[]>([])
  const [times, setTimes] = useState<ResponseTimes | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  /*
     The window, in the BROWSER'S timezone.

     "Last 7 days" means seven local days to the person reading it. Subtracting
     `7 * 86_400_000` from `now` and sending that gives a window that starts mid-afternoon
     seven days ago, and — worse for the trend, whose buckets are calendar dates — slicing
     an ISO string at 10 characters yields the UTC date, which in Nairobi (UTC+3) is the
     previous day for everything before 03:00. Incidents reported late at night landed in
     yesterday's bar.

     So: local midnight to local end-of-day. The `Instant` params get the true UTC instants
     of those local boundaries; the `LocalDate` params get the local calendar dates.
  */
  const range = useMemo(() => {
    if (days === 0) {
      return { from: null, to: null, fromDay: null, toDay: null } as {
        from: string | null; to: string | null; fromDay: string | null; toDay: string | null
      }
    }
    const end = new Date()
    end.setHours(23, 59, 59, 999)
    const start = new Date(end)
    start.setDate(start.getDate() - (days - 1))
    start.setHours(0, 0, 0, 0)
    return {
      from: start.toISOString(),
      to: end.toISOString(),
      fromDay: localDay(start),
      toDay: localDay(end),
    }
  }, [days])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // The instant window for everything that filters on a timestamp…
      const window = {
        from: range.from, to: range.to,
        severity: severity ?? undefined, category: category ?? undefined,
      }
      const [s, t, g, src, tg, rt, mk] = await Promise.all([
        analyticsSummary(window),
        // …and the LOCAL calendar dates for the trend, whose buckets are days.
        analyticsTrend({
          bucket: 'DAY', from: range.fromDay, to: range.toDay,
          severity: severity ?? undefined, category: category ?? undefined,
        }),
        analyticsGeography({ level: 'COUNTY', ...window }),
        analyticsSources(window),
        analyticsTags(window),
        analyticsResponseTimes(window),
        // The register filter, not the analytics one — markers come from the incident rows
        // themselves, which is the only place a coordinate lives.
        mapMarkers({
          from: range.from, to: range.to,
          severity: severity ?? undefined, category: category ?? undefined, size: 500,
        }),
      ])
      setSummary(s); setTrend(t); setGeography(g); setSources(src); setTags(tg); setTimes(rt)
      setMarkers(mk)
    } catch (err) {
      setError(explain(err))
    } finally {
      setLoading(false)
    }
  }, [range, severity, category])

  // `range`, `severity` and `category` are all listed. With `[]` this would load once
  // against the first render's window and never respond to any control.
  useEffect(() => { void load() }, [load])

  const findings = useMemo(
    () => (summary ? advise({ summary, trend, geography, sources, times, days }) : []),
    [summary, trend, geography, sources, times, days])

  if (loading && !summary) return <LoadingBlock label="Loading the desk's numbers…" />

  return (
    <div className="space-y-4">
      <ErrorStrip message={error} className="rounded-md border" />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {RANGES.map((r) => (
            <button key={r.days} type="button" onClick={() => setDays(r.days)}
              className={cn(
                'h-9 px-3 rounded-md border text-erp-sm',
                days === r.days
                  ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
                  : 'border-input bg-background text-muted-foreground hover:text-foreground',
              )}>
              {r.label}
            </button>
          ))}
        </div>

        {/* Both narrow every request, so no panel on the page can disagree with another. */}
        <select value={severity ?? ''} onChange={(e) => setSeverity(e.target.value || null)}
          aria-label="Filter by severity"
          className="h-9 rounded-md border border-input bg-background px-2 text-erp-sm">
          <option value="">Any severity</option>
          {SEVERITY_ORDER.map((c) => <option key={c} value={c}>{label(c)}</option>)}
        </select>

        <select value={category ?? ''} onChange={(e) => setCategory(e.target.value || null)}
          aria-label="Filter by category"
          className="h-9 rounded-md border border-input bg-background px-2 text-erp-sm">
          <option value="">Any category</option>
          {Object.keys(summary?.byCategory ?? {}).sort()
            .map((c) => <option key={c} value={c}>{label(c)}</option>)}
        </select>

        {(severity || category) && (
          <button type="button" onClick={() => { setSeverity(null); setCategory(null) }}
            className="h-9 px-2 text-erp-xs text-muted-foreground hover:text-foreground underline">
            Clear filters
          </button>
        )}

        {loading && <span className="text-erp-xs text-muted-foreground">Refreshing…</span>}
      </div>

      {summary && <Kpis s={summary} />}

      <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} title={t.hint}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-2 text-erp-sm rounded-t-md border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-chocolate text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50',
            )}>
            {t.label}
            {t.id === 'council' && findings.some((f) => f.severity === 'critical') && (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-erp-xs px-1.5 min-w-[18px] h-[18px] tabular-nums">
                {findings.filter((f) => f.severity === 'critical').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'pressure' && summary && (
        <>
          <Panel title="Daily trend"
            description="One bar per day including the quiet ones. Critical is drawn inside the total, because a critical incident is one of the total — not an extra.">
            <TrendChart points={trend} />
          </Panel>

          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))' }}>
            <ChartCard
              title="Where the work sits"
              description="Current status, in workflow order. Not a funnel — see the note."
              rows={STATUS_FLOW.map((c) => [label(c), fmtNumber(summary.byStatus[c] ?? 0)])}
              columns={['Stage', 'Incidents']}
            >
              <StatusBars byStatus={summary.byStatus} total={summary.total} />
            </ChartCard>

            <ChartCard
              title="Severity mix"
              description="Ordered LOW → CRITICAL on the ordinal ramp, never re-sorted by size."
              rows={breakdownRows(summary.bySeverity, SEVERITY_ORDER)}
              columns={['Severity', 'Incidents']}
            >
              <Donut
                slices={orderedSlices(summary.bySeverity, SEVERITY_ORDER)}
                total={summary.total}
                palette="ordinal"
                formatValue={fmtNumber}
                emptyMessage="No incidents in this window."
              />
            </ChartCard>
          </div>

          <Panel title="Impact" description="Totals across every incident in the window and scope.">
            <KpiStrip>
              <MetricCard label="Deaths" value={fmtNumber(summary.totalDeaths)} />
              <MetricCard label="Injuries" value={fmtNumber(summary.totalInjuries)} />
              <MetricCard label="Arrests" value={fmtNumber(summary.totalArrests)} />
              <MetricCard label="People affected" value={fmtNumber(summary.totalVictims)} />
              <MetricCard label="Voters affected" value={fmtNumber(summary.totalVoterImpact)} />
            </KpiStrip>
          </Panel>
        </>
      )}

      {tab === 'geography' && (
        <>
          <Panel title="Every incident that carries a coordinate"
            description="Position from the report's own location. Colour and size both carry severity.">
            <IncidentMap markers={markers} selected={selected} onSelect={setSelected} />
          </Panel>

          <ChartCard
            title="By county"
            description="Length encodes; the name is always visible. This is where reports COME FROM, which is not the same as where incidents happen."
            rows={geography.map((g) => [g.unitName ?? g.unitPath, fmtNumber(g.total),
              fmtNumber(g.critical), fmtNumber(g.deaths)])}
            columns={['County', 'Incidents', 'Critical', 'Deaths']}
          >
            <div className="p-4">
              <RankedBars
                points={geography.slice(0, 12).map((g) => ({
                  label: g.unitName ?? g.unitPath,
                  value: g.total,
                  hint: `${g.critical} critical · ${g.deaths} deaths`,
                }))}
                tone={(name) => {
                  const g = geography.find((x) => (x.unitName ?? x.unitPath) === name)
                  return g && g.critical > 0 ? 'bad' : undefined
                }}
                emptyMessage="No incident in this window recorded a unit, so there is nothing to place."
              />
            </div>
          </ChartCard>
        </>
      )}

      {tab === 'patterns' && summary && (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))' }}>
          <ChartCard
            title="Incidents by category"
            description="Identity, so the categorical palette. Top six by count; the tail folds into Other."
            rows={breakdownRows(summary.byCategory)}
            columns={['Category', 'Incidents']}
          >
            <Donut
              slices={toSlices(summary.byCategory)}
              total={summary.total}
              palette="categorical"
              formatValue={fmtNumber}
              emptyMessage="No incidents in this window, so there is nothing to break down."
            />
          </ChartCard>

          <ChartCard
            title="Verification mix"
            description="How much the desk believes what it holds — a separate axis from status. Unverified means unchecked, never false."
            rows={breakdownRows(summary.byVerification, VERIFICATION_ORDER)}
            columns={['Verification', 'Incidents']}
          >
            <Donut
              slices={orderedSlices(summary.byVerification, VERIFICATION_ORDER)}
              total={summary.total}
              palette="ordinal"
              formatValue={fmtNumber}
              emptyMessage="No incidents in this window."
            />
          </ChartCard>

          <ChartCard
            title="Reported by"
            description="Source outlets. Combined bylines are split, so “Nation / Standard” counts for both."
            rows={splitOutlets(sources).map((x) => [x.outlet, fmtNumber(x.total)])}
            columns={['Outlet', 'Incidents']}
          >
            <div className="p-4">
              <RankedBars
                points={splitOutlets(sources).slice(0, 12)
                  .map((x) => ({ label: x.outlet, value: x.total }))}
                emptyMessage="No incident in this window names a source outlet."
              />
            </div>
          </ChartCard>

          <ChartCard
            title="Tags"
            description="Weighted by count — not a word cloud. Every chip carries its number."
            rows={tags.map((t) => [t.tag, fmtNumber(t.total)])}
            columns={['Tag', 'Incidents']}
          >
            <TagChips rows={tags} />
          </ChartCard>
        </div>
      )}

      {tab === 'response' && summary && (
        <>
          <KpiStrip>
            <MetricCard label="Median to acknowledge"
              value={summary.medianHoursToAcknowledge == null ? '—' : `${summary.medianHoursToAcknowledge.toFixed(1)}h`}
              sub={`${fmtNumber(times?.acknowledgedCount ?? 0)} acknowledged`} />
            <MetricCard label="Median to resolve"
              value={summary.medianHoursToResolve == null ? '—' : `${summary.medianHoursToResolve.toFixed(1)}h`}
              sub={`${fmtNumber(times?.resolvedCount ?? 0)} resolved`} />
            {/* The p90 is the number that gets written about; the median is the comfortable
                one. Showing them together is the whole point of this tab. */}
            <MetricCard label="Slowest tenth (p90)"
              value={summary.p90HoursToResolve == null ? '—' : `${summary.p90HoursToResolve.toFixed(1)}h`}
              accent={summary.p90HoursToResolve != null && summary.medianHoursToResolve != null
                && summary.p90HoursToResolve > summary.medianHoursToResolve * 4}
              sub="what an opponent will quote" />
            <MetricCard label="Past the ageing threshold"
              value={fmtNumber(summary.openOlderThan48h)} accent={summary.openOlderThan48h > 0}
              sub="open and untouched" />
            <MetricCard label="Escalated" value={fmtNumber(summary.escalated)}
              sub="referred to a body that can act" />
            <MetricCard label="Disputed" value={fmtNumber(summary.disputed)}
              sub="a finding somebody contests" />
          </KpiStrip>

          <ChartCard
            title="Response times"
            description="Median hours from report to resolution, by week. Null under the sample floor — a median over three rows is noise."
            rows={(times?.series ?? []).map((x) => [dayLabel(x.bucket),
              x.medianHoursToResolve == null ? 'too few to say' : `${x.medianHoursToResolve.toFixed(1)} h`,
              fmtNumber(x.count)])}
            columns={['Week', 'Median to resolve', 'Resolved']}
          >
            <ResponseTimeChart times={times} />
          </ChartCard>
        </>
      )}

      {tab === 'council' && summary && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <CouncilBoard
              findings={findings}
              title="What the desk is saying"
              description={`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} over ${days === 0 ? 'every incident on record' : `the last ${days} days`}, ranked by the incidents each is worth.`}
              stakeUnit="incident"
              emptyMessage="Nothing on this desk crosses a threshold. Widen the range if you are checking whether something has been building."
            />
          </div>
          <AskPanel
            storageKey={`incidents-council-history:${days}:${severity ?? 'all'}:${category ?? 'all'}`}
            description="Plain language. The desk's figures go with the question, so the answer is about these incidents."
            placeholder="What should the desk work on first?"
            scopeNote="It sees the counts, the geography and the response times on this page — not the incident narratives."
            suggestions={[
              'What should the desk work on first?', 'What can we actually stand behind?',
              'Where is this concentrated?', 'Is this getting worse?',
              'What is stuck in the queue?',
            ]}
            onAsk={(q) => ask(q, findings)}
            context={() => incidentContext(
              { summary, trend, geography, sources, times, days }, findings)}
          />
        </div>
      )}
    </div>
  )
}

// ── KPI strip ───────────────────────────────────────────────────────────────

function Kpis({ s }: { s: AnalyticsSummary }) {
  const critical = s.bySeverity.CRITICAL ?? 0
  return (
    <KpiStrip>
      <MetricCard label="Incidents" value={fmtNumber(s.total)}
        sub={`${fmtNumber(s.last7Days)} in the last 7 days`} />
      <MetricCard label="Critical" value={fmtNumber(critical)} accent={critical > 0}
        sub="Severity, not urgency" />
      <MetricCard label="Open or investigating"
        value={fmtNumber(s.open + s.investigating)}
        sub={`${fmtNumber(s.openOlderThan48h)} older than the ageing threshold`} />
      <MetricCard label="Escalated" value={fmtNumber(s.escalated)}
        sub="Referred to a body that can act" />
      <MetricCard label="Verified share"
        value={s.verifiedShare == null ? '—' : `${Math.round(s.verifiedShare * 100)}%`}
        sub={s.verifiedShare == null ? 'Nothing to divide yet' : 'Verified or corroborated'} />
      <MetricCard label="Median to acknowledge"
        value={s.medianHoursToAcknowledge == null ? '—' : `${s.medianHoursToAcknowledge.toFixed(1)}h`}
        sub={s.medianHoursToAcknowledge == null ? 'Too few to say' : 'From report to first look'} />
      <MetricCard label="Median to resolve"
        value={s.medianHoursToResolve == null ? '—' : `${s.medianHoursToResolve.toFixed(1)}h`}
        sub={s.medianHoursToResolve == null
          ? 'Too few to say'
          : s.p90HoursToResolve == null ? undefined : `p90 ${s.p90HoursToResolve.toFixed(1)}h`} />
      <MetricCard label="My drafts" value={fmtNumber(s.draftsMine)}
        sub="Unsubmitted, and yours alone" />
    </KpiStrip>
  )
}

/**
 * Current status, in workflow order, on the ordinal ramp.
 *
 * ## Why this is NOT a funnel
 *
 * A funnel says each stage is a subset of the one above it, and prints the drop between
 * them as the information. `byStatus` is a snapshot of where each incident is RIGHT NOW,
 * and an incident sits in exactly one of these — so an incident that reached RESOLVED is
 * counted only there and not in OPEN. Rendering that as a funnel produces sentences like
 * "50% did not continue" between two stages that were never nested, which is arithmetic
 * on unrelated numbers.
 *
 * The order is still the information, so the ramp is ordinal and the stages are never
 * re-sorted by size. Each bar carries its stage name and its count in text.
 */
function StatusBars({ byStatus, total }: { byStatus: Record<string, number>; total: number }) {
  const max = Math.max(...STATUS_FLOW.map((c) => byStatus[c] ?? 0), 1)
  if (total === 0) {
    return (
      <EmptyState title="Nothing on the desk in this window"
        hint="No submitted incident falls inside the range and scope selected." />
    )
  }
  return (
    <div className="p-4">
      <ul className="space-y-1.5">
        {STATUS_FLOW.map((code, i) => {
          const value = byStatus[code] ?? 0
          return (
            <li key={code} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2">
              <span className="truncate text-erp-xs text-muted-foreground">{label(code)}</span>
              <span className="h-2.5 bg-muted/60 rounded-sm overflow-hidden">
                <span className="block h-full rounded-r-sm"
                  style={{
                    width: `${Math.max(value === 0 ? 0 : 2, (value / max) * 100)}%`,
                    background: `var(--ordinal-${Math.min(i + 1, 5)})`,
                  }} />
              </span>
              <span className="text-erp-xs tabular-nums w-10 text-right">{fmtNumber(value)}</span>
            </li>
          )
        })}
      </ul>
      <p className="mt-3 text-erp-xs text-muted-foreground/80">
        Each incident is counted once, in the state it is in now. These stages do not nest,
        so the gaps between them are not drop-off.
      </p>
    </div>
  )
}

// ── the trend ───────────────────────────────────────────────────────────────

/**
 * One bar per day, critical drawn inside the total.
 *
 * A day with no incidents is a bar of height zero that is still THERE, because a trend
 * that silently omits quiet days draws a lie — the reader infers a continuous run of
 * activity from a row of adjacent bars that are weeks apart.
 */
function TrendChart({ points }: { points: TrendPoint[] }) {
  const [table, setTable] = useState(false)
  const peak = Math.max(...points.map((p) => p.total), 0)
  const max = Math.max(peak, 1)

  if (points.length === 0) {
    return (
      <EmptyState title="Nothing in this window"
        hint="No incident was reported in the range and scope selected. Widen the range, or check the register." />
    )
  }

  if (table) {
    return (
      <div>
        <ToggleRow table={table} onToggle={setTable} />
        <SimpleTable
          columns={['Day', 'Incidents', 'Critical']}
          rows={points.map((p) => [dayLabel(p.bucket), fmtNumber(p.total), fmtNumber(p.critical)])}
        />
      </div>
    )
  }

  return (
    <div>
      <ToggleRow table={table} onToggle={setTable} />
      <ChartFrame title="Incidents per day" description={`Peak ${fmtNumber(peak)}`}>
        <div className="flex items-end gap-[2px] h-40 border-b border-border" role="img"
          aria-label={`Daily incident counts, ${points.length} days, peak ${peak}`}>
          {points.map((p) => (
            <div key={p.bucket} className="flex-1 min-w-0 h-full flex items-end justify-center"
              title={`${p.bucket}: ${p.total} incidents, ${p.critical} critical`}>
              <div className="w-full max-w-[18px] flex flex-col justify-end h-full">
                {/* The critical portion sits at the base of the SAME bar. A 2px surface gap
                    separates it from the rest so the boundary is a gap, not a second colour
                    to perceive. */}
                <div className="rounded-t-[4px]"
                  style={{
                    height: `${((p.total - p.critical) / max) * 100}%`,
                    background: 'var(--ordinal-2)',
                  }} />
                {p.critical > 0 && <div className="h-[2px] bg-card shrink-0" />}
                <div style={{
                  height: `${(p.critical / max) * 100}%`,
                  background: 'var(--ordinal-5)',
                }} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-erp-xs text-muted-foreground/70 mt-1">
          <span>{dayLabel(points[0]?.bucket)}</span>
          <span>{dayLabel(points[points.length - 1]?.bucket)}</span>
        </div>
        <ul className="mt-2 flex gap-4 text-erp-xs">
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: 'var(--ordinal-2)' }} />
            Everything else
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: 'var(--ordinal-5)' }} />
            Critical (part of the same total)
          </li>
        </ul>
      </ChartFrame>
    </div>
  )
}

// ── response times ──────────────────────────────────────────────────────────

/** A single series, so no legend — the panel title names it. */
function ResponseTimeChart({ times }: { times: ResponseTimes | null }) {
  const series = (times?.series ?? []).filter((p) => p.medianHoursToResolve != null)
  if (series.length < 2) {
    return (
      <EmptyState
        title="Not enough resolved incidents to plot"
        hint={`${fmtNumber(times?.resolvedCount ?? 0)} resolved in this window. A median needs a handful of rows before it says anything, so the server returns nothing rather than noise.`}
      />
    )
  }

  const values = series.map((p) => p.medianHoursToResolve!)
  const max = Math.max(...values, 1)
  const path = series.map((p, i) => {
    const x = (i / (series.length - 1)) * 100
    const y = 100 - (p.medianHoursToResolve! / max) * 100
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
  }).join(' ')

  return (
    <div className="p-4">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-32"
        role="img" aria-label={`Median hours to resolve, ${series.length} weeks, peak ${max.toFixed(1)} hours`}>
        <path d={path} fill="none" stroke="var(--chart-1)" strokeWidth="2"
          vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div className="flex justify-between text-erp-xs text-muted-foreground/70 mt-1">
        <span>{dayLabel(series[0]!.bucket)} · {values[0]!.toFixed(1)} h</span>
        <span>peak {max.toFixed(1)} h</span>
        <span>{dayLabel(series[series.length - 1]!.bucket)} · {values[values.length - 1]!.toFixed(1)} h</span>
      </div>
    </div>
  )
}

// ── tags ────────────────────────────────────────────────────────────────────

/**
 * Weighted chips, not a word cloud.
 *
 * A word cloud encodes magnitude in area, which people read badly, and in a layout that
 * changes on every render. A chip carrying its own number is readable, sortable and
 * stable; the weight only nudges the type size.
 */
function TagChips({ rows }: { rows: TagRow[] }) {
  if (rows.length === 0) {
    return (
      <EmptyState title="No tags yet"
        hint="Tags are added on the report's evidence step. They are what makes a pattern findable later." />
    )
  }
  const max = Math.max(...rows.map((r) => r.total), 1)
  return (
    <div className="p-4 flex flex-wrap gap-2">
      {rows.slice(0, 40).map((r) => (
        <span key={r.tag}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1"
          style={{ fontSize: `${0.72 + 0.28 * (r.total / max)}rem` }}>
          {r.tag}
          <span className="tabular-nums text-muted-foreground">{fmtNumber(r.total)}</span>
        </span>
      ))}
    </div>
  )
}

// ── the table toggle every chart owes the reader ────────────────────────────

/**
 * A chart and the same numbers as a table.
 *
 * Not an accessibility afterthought: three of the light categorical steps sit under 3:1 on
 * the card surface, and the validator's contrast warning is discharged by relief — visible
 * values, or a table. This is the table.
 */
function ChartCard({
  title, description, rows, columns, children,
}: {
  title: string
  description?: string
  rows: (string | number)[][]
  columns: string[]
  children: ReactNode
}) {
  const [table, setTable] = useState(false)
  return (
    <Panel title={title} description={description} bodyClassName="p-0"
      right={<ToggleButton table={table} onToggle={setTable} />}>
      {table ? <SimpleTable columns={columns} rows={rows} /> : children}
    </Panel>
  )
}

function ToggleRow({ table, onToggle }: { table: boolean; onToggle: (v: boolean) => void }) {
  return (
    <div className="flex justify-end mb-2">
      <ToggleButton table={table} onToggle={onToggle} />
    </div>
  )
}

function ToggleButton({ table, onToggle }: { table: boolean; onToggle: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onToggle(!table)}
      aria-pressed={table}
      title={table ? 'Show the chart' : 'Show the numbers as a table'}
      className="h-8 px-2 inline-flex items-center gap-1.5 rounded-md border border-input text-erp-xs text-muted-foreground hover:text-foreground">
      {table ? <BarChart3 className="h-3.5 w-3.5" /> : <Table2 className="h-3.5 w-3.5" />}
      {table ? 'Chart' : 'Table'}
    </button>
  )
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: (string | number)[][] }) {
  if (rows.length === 0) {
    return <div className="px-4 py-8 text-center text-erp-sm text-muted-foreground">Nothing to show.</div>
  }
  return (
    <div className="axis-table-scroll max-h-80 overflow-y-auto">
      <table className="w-full border-collapse">
        <thead><tr>{columns.map((c) => (
          <th key={c} className="erp-grid-header text-left whitespace-nowrap">{c}</th>
        ))}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td key={j} className={cn('erp-grid-cell', j > 0 && 'text-right tabular-nums')}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── dates, in the reader's own timezone ─────────────────────────────────────

/** A Date → `YYYY-MM-DD` in LOCAL time. `toISOString().slice(0,10)` gives the UTC day. */
function localDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * A server `LocalDate` → the reader's short date.
 *
 * Parsed as `YYYY-MM-DDT00:00:00` (no `Z`), which JavaScript reads as LOCAL midnight.
 * `new Date('2026-08-24')` is parsed as UTC midnight and renders as the 23rd anywhere west
 * of Greenwich — the same off-by-one this page was fixing.
 */
function dayLabel(bucket?: string | null): string {
  if (!bucket) return '—'
  const d = new Date(`${bucket}T00:00:00`)
  return Number.isNaN(d.getTime())
    ? bucket
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

// ── shaping ─────────────────────────────────────────────────────────────────

/** Identity breakdown → donut slices. The id is the CODE, so colour follows the entity. */
function toSlices(breakdown: Record<string, number>): DonutSlice[] {
  return Object.entries(breakdown).map(([code, value]) => ({
    id: code, label: label(code), value,
  }))
}

/** An ordered breakdown, in the SCALE's order — never sorted by size. */
function orderedSlices(breakdown: Record<string, number>, scale: string[]): DonutSlice[] {
  return inScaleOrder(Object.keys(breakdown), scale)
    .map((code) => ({ id: code, label: label(code), value: breakdown[code] ?? 0 }))
}

function breakdownRows(breakdown: Record<string, number>, scale?: string[]): (string | number)[][] {
  const codes = scale
    ? inScaleOrder(Object.keys(breakdown), scale)
    : Object.keys(breakdown).sort((a, b) => (breakdown[b] ?? 0) - (breakdown[a] ?? 0))
  return codes.map((c) => [label(c), fmtNumber(breakdown[c] ?? 0)])
}

/**
 * "Nation / Standard" is two outlets, and counting it as one invents a publication.
 *
 * PollSphere splits on `/` for the same reason. Totals are summed after splitting, so an
 * outlet that appears both alone and in a combined byline is one row.
 */
function splitOutlets(rows: SourceRow[]): SourceRow[] {
  const totals = new Map<string, number>()
  for (const row of rows) {
    for (const part of row.outlet.split('/').map((p) => p.trim()).filter(Boolean)) {
      totals.set(part, (totals.get(part) ?? 0) + row.total)
    }
  }
  return [...totals.entries()]
    .map(([outlet, total]) => ({ outlet, total }))
    .sort((a, b) => b.total - a.total)
}
